import { useCallback, useEffect, useRef, useState } from "react";

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

type SpeechRecognitionAlternativeLike = {
  transcript: string;
};

type SpeechRecognitionResultLike = {
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternativeLike;
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
};

type SpeechRecognitionErrorEventLike = {
  error: string;
};

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export function isSpeechRecognitionSupported() {
  if (typeof window === "undefined") return false;
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function useSpeechRecognition(options: {
  lang: string;
  onText: (text: string) => void;
  onError?: (error: string) => void;
}) {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [listening, setListening] = useState(false);
  const [starting, setStarting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const supported = isSpeechRecognitionSupported();

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setStarting(false);
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!supported) {
      options.onError?.("speech_recognition_unsupported");
      return;
    }

    setStarting(true);
    recognitionRef.current?.abort();
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = options.lang;
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      let transcript = "";
      setProcessing(true);
      for (
        let index = event.resultIndex;
        index < event.results.length;
        index += 1
      ) {
        const result = event.results[index];
        if (result?.isFinal && result[0]?.transcript) {
          transcript += result[0].transcript;
        }
      }
      const text = transcript.trim();
      if (text) {
        options.onText(text);
      }
      setProcessing(false);
    };
    recognition.onerror = (event) => {
      options.onError?.(event.error);
      setStarting(false);
      setListening(false);
    };
    recognition.onend = () => {
      setStarting(false);
      setListening(false);
      setProcessing(false)
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch (error) {
      options.onError?.(
        error instanceof Error ? error.message : "speech_recognition_failed",
      );
      setStarting(false);
      setListening(false);
    }
  }, [options, supported]);

  const toggle = useCallback(() => {
    if (listening) {
      stop();
      setProcessing(true);
    } else {
      start();
      setProcessing(false);
    }
  }, [listening, start, stop]);

  return {
    supported,
    listening,
    processing,
    active: starting || listening || processing,
    start,
    stop,
    toggle,
  };
}
